'use client';

import type { ImageLoaderProps } from 'next/image';

const SANITY_CDN = 'https://cdn.sanity.io/images/';

/**
 * Global next/image loader.
 *
 * Sanity rasters are resized by Sanity's own image CDN instead of Vercel's
 * optimizer. Two reasons, both measured against production:
 *
 *  1. `asset->url` is the UNTRANSFORMED original — some are 4 MB, 2940x1590
 *     PNGs. Routing those through /_next/image made Vercel re-download and
 *     re-encode the full original on every cold transform; under crawler
 *     concurrency the transforms timed out and the images were reported dead.
 *     Sanity serves the same asset pre-sized, so there is nothing to time out.
 *
 *  2. /_next/image content-negotiates on Accept. Any client that does not
 *     advertise AVIF/WebP (curl, most crawlers, social unfurlers) fell through
 *     to the ORIGINAL png — 910 KB where a browser got 126 KB. `fm=webp` is
 *     unconditional, so every client gets the small encoding.
 *
 * `fm=webp` rather than `auto=format`: auto=format also keys off Accept and
 * regresses to png for a wildcard Accept header. avif is rejected outright by
 * this Sanity endpoint (it answers with a JSON error), so webp is the target.
 *
 * SVGs pass through untouched — a width/format transform would rasterize the
 * logos and icons stored in Sanity.
 */
export default function imageLoader({ src, width, quality }: ImageLoaderProps): string {
  if (!src.startsWith(SANITY_CDN)) {
    // Vercel serves /_next/image at the platform level. A self-hosted
    // `next dev` / `next start` does NOT once `images.loader` is 'custom' —
    // the endpoint 404s, so every /public image renders broken while
    // production is fine.
    //
    // This used to test `NODE_ENV === 'development'`, which covers `next dev`
    // and misses `next start` — the case the sentence above names, and the one
    // every local verification actually uses. A production build served
    // locally rendered zero /public images: the hero came back as 113,985 B of
    // 404 document instead of 8,910 B of WebP, which is why screenshots and
    // LCP runs had to be taken from production (finding seo-h3nbo0).
    //
    // `SITE_HAS_IMAGE_OPTIMIZER` is inlined by next.config.ts from
    // `process.env.VERCEL`, because this is a client module and cannot read it
    // directly. Off Vercel, serve the original file: unoptimized, but present.
    if (!process.env.SITE_HAS_IMAGE_OPTIMIZER) return src;
    // Local /public assets and YouTube posters keep using Vercel's optimizer.
    return `/_next/image?url=${encodeURIComponent(src)}&w=${width}&q=${quality ?? 75}`;
  }

  if (src.endsWith('.svg')) return src;

  const url = new URL(src);
  url.searchParams.set('w', String(width));
  url.searchParams.set('q', String(quality ?? 75));
  url.searchParams.set('fm', 'webp');
  // Never upscale past the source, and preserve aspect ratio.
  url.searchParams.set('fit', 'max');
  return url.toString();
}
